import { z } from 'zod';

/**
 * Settings belong to the kernel or to exactly one module and are either admin settings
 * (`global`, `company`) or personal settings (`user`). The kernel renders all of them from these
 * definitions (docs/architecture/modules.md, section "Einstellungen").
 */
export const SETTING_LEVELS = ['global', 'company', 'user'] as const;
export type SettingLevel = (typeof SETTING_LEVELS)[number];

/** Sections of the profile page that modules can contribute personal settings to. */
export const PROFILE_SECTIONS = [
  'general',
  'appearance',
  'notifications',
  'accessibility',
] as const;
export type ProfileSection = (typeof PROFILE_SECTIONS)[number];

interface SettingBase {
  /** `<module>.<name>`, e.g. `tickets.defaultPriority`. */
  key: string;
  level: SettingLevel;
  /** Only for `user` settings. `profile` lists the setting in the kernel profile section. */
  placement?: 'module' | 'profile';
  /** A profile section for `placement: 'profile'`, otherwise a group on the module settings page. */
  section?: string;
}

export interface BooleanSetting extends SettingBase {
  type: 'boolean';
  default: boolean;
}

export interface SelectSetting extends SettingBase {
  type: 'select';
  options: readonly string[];
  default: string;
}

export interface NumberSetting extends SettingBase {
  type: 'number';
  default: number;
  min?: number;
  max?: number;
  integer?: boolean;
}

export interface TextSetting extends SettingBase {
  type: 'text';
  default: string;
  maxLength?: number;
}

/** A path from the navigation the user can see, e.g. the start page. */
export interface NavigationTargetSetting extends SettingBase {
  type: 'navigation-target';
  default: string;
}

/**
 * Order and visibility of the items of a page, e.g. the tiles of the start page. The page that
 * shows the items edits the value; the settings pages do not list it.
 */
export interface ArrangementSetting extends SettingBase {
  type: 'arrangement';
  default: Arrangement;
}

/** Keys in the order the person chose, and the keys the person hid. */
export interface Arrangement {
  order: readonly string[];
  hidden: readonly string[];
}

export type SettingDefinition =
  | BooleanSetting
  | SelectSetting
  | NumberSetting
  | TextSetting
  | NavigationTargetSetting
  | ArrangementSetting;

export type SettingValue = boolean | string | number | Arrangement;

export type CatalogSetting = SettingDefinition & { owner: string };

const NAVIGATION_PATH = /^\/[a-z0-9/_-]*$/;
const DEFAULT_TEXT_LENGTH = 500;
// Tile keys like `core.reminders` or module ids like `crm`.
const ARRANGEMENT_KEY = /^[a-z][a-z0-9-]*(\.[a-zA-Z0-9-]+)*$/;
const MAX_ARRANGED = 100;

/** Built on call like the other schemas, so bundles that never validate leave zod out. */
export function arrangementSchema() {
  const keys = z.array(z.string().max(100).regex(ARRANGEMENT_KEY)).max(MAX_ARRANGED);
  return z.strictObject({ order: keys, hidden: keys });
}

export function settingValueSchema(setting: SettingDefinition): z.ZodType<SettingValue> {
  switch (setting.type) {
    case 'boolean':
      return z.boolean();
    case 'select':
      return z.string().refine((value) => setting.options.includes(value), {
        error: 'Unknown option',
      });
    case 'number': {
      let schema = setting.integer ? z.number().int() : z.number();
      if (setting.min !== undefined) schema = schema.min(setting.min);
      if (setting.max !== undefined) schema = schema.max(setting.max);
      return schema;
    }
    case 'text':
      return z.string().max(setting.maxLength ?? DEFAULT_TEXT_LENGTH);
    case 'navigation-target':
      return z.string().regex(NAVIGATION_PATH);
    case 'arrangement':
      return arrangementSchema();
  }
}

/** Settings the kernel lists on the profile, module and admin settings pages. */
export function listedOnSettingsPages(setting: SettingDefinition): boolean {
  return setting.type !== 'arrangement';
}

/**
 * Applies an arrangement to the items a person can see: first the keys of `order` in that order,
 * then new items in their own order. Unknown keys, e.g. of a module switched off, are skipped.
 */
export function arrange<T extends { key: string }>(
  items: readonly T[],
  arrangement: Arrangement,
): { visible: T[]; hidden: T[] } {
  const rank = new Map(arrangement.order.map((key, index) => [key, index]));
  const sorted = items
    .map((item, index) => ({ item, index, rank: rank.get(item.key) ?? Infinity }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ item }) => item);
  const hidden = new Set(arrangement.hidden);
  return {
    visible: sorted.filter((item) => !hidden.has(item.key)),
    hidden: sorted.filter((item) => hidden.has(item.key)),
  };
}

/** The part of the key after the owner, e.g. `defaultPriority` for `tickets.defaultPriority`. */
export function settingName(setting: Pick<CatalogSetting, 'key' | 'owner'>): string {
  return setting.key.slice(setting.owner.length + 1);
}

/** i18n prefix: `<owner>.settings.<name>` with `.label`, `.description` and `.options.<value>`. */
export function settingI18nKey(setting: Pick<CatalogSetting, 'key' | 'owner'>): string {
  return `${setting.owner}.settings.${settingName(setting)}`;
}

/**
 * Personal settings fall back to the company default an admin set, then to the definition.
 * Stored values that no longer fit the definition (e.g. a removed option) are skipped.
 */
export function resolveSettingValue(
  setting: SettingDefinition,
  stored: { value?: unknown; companyDefault?: unknown },
): SettingValue {
  const schema = settingValueSchema(setting);
  for (const candidate of [stored.value, stored.companyDefault]) {
    const parsed = schema.safeParse(candidate);
    if (candidate !== undefined && parsed.success) return parsed.data;
  }
  return setting.default;
}
